import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * /verify-email?token=...
 *
 * Stage 2 of two-stage signup. POSTs the token to /auth/verify-email; on
 * success the backend mints a session cookie + we redirect into the app. On
 * failure we surface the backend's friendly error so the user knows whether
 * to resend or sign up again.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  // Tokens are single-use. React 18 StrictMode (and even back-button replays)
  // can fire this effect twice — the second call always 400s on a consumed
  // token, briefly flashing the error UI before the success redirect lands.
  // A ref guard keeps the first invocation authoritative.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = params.get("token");
    if (!token) {
      setState("error");
      setErrorMsg("Missing verification token. Please use the link from your email.");
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        setState("success");
        // Brief celebratory pause before dropping the user into the app.
        setTimeout(() => navigate("/generator"), 1800);
      } catch (e) {
        const detail = e.response?.data?.detail || "";
        // If a previous tab already verified this account, the backend sends
        // "Email already verified. Please log in." — treat as a soft success
        // and route to login instead of flashing a scary error.
        if (/already verified/i.test(detail)) {
          setState("success");
          setErrorMsg("");
          setTimeout(() => navigate("/login"), 1500);
          return;
        }
        setState("error");
        setErrorMsg(
          detail ||
          "We couldn't verify this link. It may have expired or already been used."
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md border-gray-200 shadow-lg rounded-2xl">
        <CardContent className="p-8 text-center">
          {state === "verifying" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Loader2 className="w-6 h-6 text-gray-600 animate-spin" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Verifying your email…</h1>
              <p className="text-sm text-gray-500">Hang on a sec.</p>
            </>
          )}
          {state === "success" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">You're all set!</h1>
              <p className="text-sm text-gray-600">
                Email confirmed. Taking you to Slides by Wokelo…
              </p>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Verification failed</h1>
              <p className="text-sm text-gray-600">{errorMsg}</p>
              <div className="mt-6 space-y-2">
                <Button onClick={() => navigate("/signup")} className="w-full" data-testid="verify-back-signup">
                  Sign up again
                </Button>
                <Button onClick={() => navigate("/login")} variant="ghost" className="w-full text-gray-500 hover:text-gray-900">
                  Back to login
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
